import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, inputCls } from "@/components/LegsEditor";
import { categoryLabel, money } from "@/lib/format";
import { useApi, errMsg, downloadBlob } from "@/lib/api";

function initialSelection(options, presentation, initialIds) {
  const existing = Object.fromEntries((presentation?.items || []).map((it) => [it.option_id, it]));
  return Object.fromEntries(options.map((o) => {
    const it = existing[o.id];
    const checked = presentation ? !!it : initialIds?.length ? initialIds.includes(o.id) : true;
    return [o.id, { checked, price: it ? it.client_price : (o.customer_price || o.price), note: it?.client_note || "" }];
  }));
}

export function allIn(base, legs, form) {
  const b = Number(base || 0);
  if (!form.all_in) return { fet: 0, seg: 0, total: b };
  const paxSegments = legs.reduce((n, l) => n + Number(l.pax || 0), 0);
  const fet = Math.round(b * Number(form.fet_rate || 0)) / 100;
  const seg = Math.round(Number(form.segment_fee || 0) * paxSegments * 100) / 100;
  return { fet, seg, paxSegments, total: Math.round((b + fet + seg) * 100) / 100 };
}

function OptionRow({ o, s, onChange, form, legs }) {
  const margin = Number(s.price || 0) - o.price;
  const t = allIn(s.price, legs, form);
  return (
    <div className={`lux-card p-4 transition-opacity ${s.checked ? "" : "opacity-50"}`} data-testid={`pres-option-${o.rank}`}>
      <div className="flex items-start gap-3">
        <Checkbox checked={s.checked} onCheckedChange={(v) => onChange({ checked: !!v })} className="mt-1" data-testid={`pres-option-${o.rank}-checkbox`} />
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm">Option {o.rank} · {o.aircraft_type}</p>
          <p className="text-[11px] text-zinc-500 mt-0.5">{categoryLabel(o.aircraft_category)}{o.seats ? ` · ${o.seats} seats` : ""} · Your cost {money(o.price, o.currency)}</p>
          {s.checked && (
            <div className="grid sm:grid-cols-[160px_1fr] gap-3 mt-3">
              <Field label="Client price">
                <Input type="number" step="1" min="1" className={inputCls} value={s.price} onChange={(e) => onChange({ price: e.target.value })} data-testid={`pres-option-${o.rank}-price-input`} />
              </Field>
              <Field label="Note for client (optional)">
                <Input className={inputCls} value={s.note} onChange={(e) => onChange({ note: e.target.value })} placeholder="Newest cabin, Wi-Fi onboard" data-testid={`pres-option-${o.rank}-note-input`} />
              </Field>
            </div>
          )}
          {s.checked && (
            <p className={`text-[11px] mt-2 ${margin < 0 ? "text-rose-300" : "text-emerald-300/80"}`} data-testid={`pres-option-${o.rank}-margin`}>
              {margin < 0 ? "Below your cost by " : "Your markup: "}{money(Math.abs(margin), o.currency)}
            </p>
          )}
          {s.checked && (form.all_in ? (
            <dl className="mt-3 max-w-xs text-xs border-t border-white/[0.08] pt-2" data-testid={`pres-option-${o.rank}-allin`}>
              <div className="flex justify-between py-0.5 text-zinc-400"><dt>Charter price</dt><dd className="text-zinc-200">{money(Number(s.price || 0), o.currency)}</dd></div>
              <div className="flex justify-between py-0.5 text-zinc-400"><dt>Taxes & fees</dt><dd className="text-zinc-200">{money(t.fet + t.seg, o.currency)}</dd></div>
              <div className="flex justify-between pt-1 mt-1 border-t border-white/[0.08] text-white"><dt>Client all-in total</dt><dd>{money(t.total, o.currency)}</dd></div>
            </dl>
          ) : (
            <p className="text-[11px] text-zinc-500 mt-1" data-testid={`pres-option-${o.rank}-plus-taxes`}>Client sees {money(Number(s.price || 0), o.currency)} plus taxes & fees</p>
          ))}
        </div>
      </div>
    </div>
  );
}

const BLANK_FORM = { client_name: "", title: "Private Jet Charter Proposal", message: "", show_photos: true, all_in: false, fet_rate: 7.5, segment_fee: 5.3 };

export default function PresentationBuilder({ open, onOpenChange, trip, presentation, initialIds, onSaved }) {
  const api = useApi();
  const [form, setForm] = useState(BLANK_FORM);
  const [sel, setSel] = useState({});
  const [pct, setPct] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(presentation
      ? { client_name: presentation.client_name || "", title: presentation.title, message: presentation.message || "", show_photos: presentation.show_photos,
          all_in: !!presentation.all_in, fet_rate: presentation.fet_rate ?? 7.5, segment_fee: presentation.segment_fee ?? 5.3 }
      : BLANK_FORM);
    setSel(initialSelection(trip.options, presentation, initialIds));
    setPct("");
  }, [open, presentation, trip.options, initialIds]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const update = (id, patch) => setSel((s) => ({ ...s, [id]: { ...s[id], ...patch } }));
  const applyMarkup = () => {
    const p = Number(pct);
    if (!p && p !== 0) return;
    setSel((s) => Object.fromEntries(trip.options.map((o) => [o.id, s[o.id].checked ? { ...s[o.id], price: Math.round(o.price * (1 + p / 100)) } : s[o.id]])));
  };

  const save = async (withPdf = false) => {
    const items = trip.options.filter((o) => sel[o.id]?.checked).map((o) => ({ option_id: o.id, client_price: Number(sel[o.id].price), client_note: sel[o.id].note }));
    if (!items.length) return toast.error("Select at least one option");
    if (items.some((i) => !(i.client_price > 0))) return toast.error("Enter a client price for each selected option");
    setSaving(true);
    try {
      const body = { ...form, fet_rate: Number(form.fet_rate || 0), segment_fee: Number(form.segment_fee || 0), items };
      const { data } = presentation ? await api.put(`/presentations/${presentation.id}`, body) : await api.post(`/trips/${trip.id}/presentations`, body);
      toast.success(presentation ? "Presentation updated" : "Client presentation created");
      if (withPdf) await downloadBlob(api, `/presentations/${data.id}/pdf`, `Charter-Proposal-${form.client_name || "Client"}.pdf`);
      onSaved(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111319] border-white/10 max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="presentation-builder">
        <DialogHeader>
          <p className="eyebrow">Client presentation</p>
          <DialogTitle className="font-serif-display text-3xl font-normal text-white">{presentation ? "Edit presentation" : "Present options to your client"}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-zinc-400 -mt-1">Your client sees your prices and your contact details only. Operator names, certificates, tail numbers and your cost are never shown.</p>
        <div className="grid sm:grid-cols-2 gap-3 mt-2">
          <Field label="Client name"><Input className={inputCls} value={form.client_name} onChange={set("client_name")} placeholder="Mr. & Mrs. Smith" data-testid="pres-client-name-input" /></Field>
          <Field label="Title"><Input className={inputCls} value={form.title} onChange={set("title")} data-testid="pres-title-input" /></Field>
        </div>
        <Field label="Message to client"><Textarea className="bg-[#0d0f14] border-white/10" value={form.message} onChange={set("message")} placeholder="Here are the best options for your trip to Palm Beach…" data-testid="pres-message-input" /></Field>
        <label className="flex items-center gap-3 text-sm text-zinc-300">
          <Switch checked={form.show_photos} onCheckedChange={set("show_photos")} data-testid="pres-show-photos-switch" />
          Include aircraft photos (exteriors are generic photos of the type, so no tail number shows)
        </label>
        <div className="flex items-end gap-3 mt-2">
          <Field label="Markup % on selected options"><Input type="number" className={`${inputCls} w-32`} value={pct} onChange={(e) => setPct(e.target.value)} placeholder="12" data-testid="pres-markup-input" /></Field>
          <button type="button" className="frame-btn !py-3" onClick={applyMarkup} data-testid="pres-apply-markup-btn">Apply</button>
        </div>
        <div className="lux-card p-4 space-y-3" data-testid="pres-allin-section">
          <label className="flex items-center gap-3 text-sm text-zinc-200">
            <Switch checked={form.all_in} onCheckedChange={set("all_in")} data-testid="pres-allin-switch" />
            Show "All-in" prices (adds Federal Excise Tax and segment fees)
          </label>
          {form.all_in && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Federal Excise Tax %"><Input type="number" step="0.1" min="0" className={inputCls} value={form.fet_rate} onChange={set("fet_rate")} data-testid="pres-fet-rate-input" /></Field>
                <Field label="Segment fee $ / pax / segment"><Input type="number" step="0.01" min="0" className={inputCls} value={form.segment_fee} onChange={set("segment_fee")} data-testid="pres-segment-fee-input" /></Field>
              </div>
              <p className="text-[11px] text-zinc-500">
                FET is applied to your client price. Segment fees = fee × passengers on each leg ({trip.legs.reduce((n, l) => n + Number(l.pax || 0), 0)} passenger segments on this trip).
                The defaults are the 2026 US domestic rates (7.5%, $5.30). Adjust them for international, Alaska/Hawaii or rural-airport trips.
              </p>
            </>
          )}
        </div>
        <div className="space-y-2">
          {trip.options.map((o) => sel[o.id] && <OptionRow key={o.id} o={o} s={sel[o.id]} form={form} legs={trip.legs} onChange={(p) => update(o.id, p)} />)}
        </div>
        <div className="flex flex-wrap justify-end gap-3 pt-2">
          <button type="button" className="frame-btn" onClick={() => onOpenChange(false)} data-testid="pres-cancel-btn">Cancel</button>
          <button type="button" className="frame-btn" onClick={() => save(true)} disabled={saving} data-testid="pres-save-pdf-btn">{saving ? "Saving…" : "Save & download PDF"}</button>
          <button type="button" className="frame-btn gold" onClick={() => save()} disabled={saving} data-testid="pres-save-btn">{saving ? "Saving…" : "Save presentation"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
